import { CSSProperties, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { GridApi } from 'ag-grid-community'
import { styled } from '@mui/system'
import { CalendarDateVO } from '../../../../../../../../../domain/value-object/CalendarDateVO'
import {
  AggregateField,
  AggregateTarget,
} from '../../../../../../../../../domain/entity/WbsItemEntity'
import { GanttDisplayUnit } from '../../../../../../../../../domain/value-object/GanttParameterVO'
import { GanttColor } from '../../../../../../../../../styles/commonStyles'
import { colorPalette } from '../../../../../../../../style/colorPalette'
import {
  calcProgressVariance,
  formatProgressRate,
  formatVarianceDiff,
  getAlertLevel,
  varianceColor,
  WeatherIcon,
} from '../CellRenderer/progressVariance'
import { useProgressLinePoints } from './useProgressLinePoints'

const OverlayLabel = styled('div')({
  display: 'inline-flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 4,
  padding: '2px 6px',
  fontWeight: 700,
  lineHeight: 1,
  whiteSpace: 'nowrap',
  color: colorPalette.monotone[10],
  backgroundColor: colorPalette.blue[0] + 'E6',
  borderRadius: 4,
  position: 'absolute',
  left: 0,
  top: '50%',
  transform: 'translateY(-50%)',
})

type ProgressLineOverlayProps = {
  gridApi: GridApi
  timeScale: CalendarDateVO[]
  aggregateTarget: AggregateTarget
  aggregateField: AggregateField
  unit: GanttDisplayUnit
}

export const ProgressLineOverlay = ({
  gridApi,
  timeScale,
  aggregateTarget,
  aggregateField,
  unit,
}: ProgressLineOverlayProps) => {
  const [containerEl, setContainerEl] = useState<Element | null>(null)
  const [containerHeight, setContainerHeight] = useState(0)

  useEffect(() => {
    const viewport = document.querySelector('.ag-center-cols-viewport')
    setContainerEl(viewport)
  }, [])

  useEffect(() => {
    if (!containerEl) return
    containerEl.classList.add('has-progress-line')
    return () => containerEl.classList.remove('has-progress-line')
  }, [containerEl])

  useEffect(() => {
    if (!containerEl) return
    const inner = containerEl.querySelector('.ag-center-cols-container')
    if (!inner) return

    const updateHeight = () => {
      setContainerHeight(inner.scrollHeight)
    }
    updateHeight()

    const observer = new ResizeObserver(() => updateHeight())
    observer.observe(inner)
    return () => observer.disconnect()
  }, [containerEl])

  const ganttColumn = gridApi.getColumn('ganttChart')
  const ganttColumnLeft = ganttColumn?.getLeft() ?? 0
  const ganttColumnWidth = ganttColumn?.getActualWidth() ?? 0

  const { points, firstRowTop, lastRowBottom, todayX } = useProgressLinePoints(
    gridApi,
    timeScale,
    ganttColumnWidth,
    aggregateTarget,
    aggregateField,
    'body',
    unit
  )

  if (!containerEl || points.length === 0 || ganttColumnWidth <= 0) {
    return null
  }

  const first = points[0]
  const last = points[points.length - 1]
  const startX = todayX ?? first.x
  const endX = todayX ?? last.x
  const extendedPoints = [
    { x: startX, y: firstRowTop },
    ...points,
    { x: endX, y: lastRowBottom },
  ]

  const svgStyle: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: ganttColumnLeft,
    width: ganttColumnWidth,
    height: containerHeight,
    pointerEvents: 'none',
    zIndex: 100,
    overflow: 'hidden',
  }

  return createPortal(
    <svg style={svgStyle}>
      <polyline
        points={extendedPoints.map(p => `${p.x},${p.y}`).join(' ')}
        fill="none"
        stroke={GanttColor.BORDER_TODAY}
        strokeWidth={3}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {points.map(p => (
        <circle
          key={`${p.x}-${p.y}`}
          cx={p.x}
          cy={p.y}
          r={2}
          fill={GanttColor.BORDER_TODAY}
        />
      ))}
      {todayX != null &&
        points.map((p, idx) => {
          if (p.isFullyComplete || !p.isSummary) return null
          const variance = calcProgressVariance(
            p.progressRate,
            p.scheduledProgressRate
          )
          if (!variance) return null
          const level = getAlertLevel(variance)
          return (
            <foreignObject
              key={`label-${idx}`}
              x={todayX + 4}
              y={p.y - 16}
              width={140}
              height={32}
              style={{ pointerEvents: 'none', overflow: 'visible' }}
            >
              <OverlayLabel>
                <WeatherIcon level={level} size={18} />
                <span style={{ fontSize: 14 }}>
                  {formatProgressRate(p.progressRate)}
                </span>
                <span
                  style={{
                    fontSize: 13,
                    color: varianceColor(variance.status),
                  }}
                >
                  {formatVarianceDiff(variance.diff)}
                </span>
              </OverlayLabel>
            </foreignObject>
          )
        })}
    </svg>,
    containerEl
  )
}
